import type { ILocationBase } from '~/client/graph'
import { SaveMaterialsDocument } from '~/client/graph/operations/generated/MaterialResources.generated'
import type { IMaterialInput } from '~/client/graph/types.generated'
import { LocationType } from '~/client/graph/types.generated'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  compactResourceData,
  mergeResourceData,
  splitResourceFormData,
} from '~/client/src/shared/hooks/splitResourceFormData'

import {
  parseMaterialResource,
  toMaterialResourceRecord,
  type MaterialResource,
  type MissingMaterialResourceId,
} from './materialResource'
import type {
  ProjectResourceFailure,
  ProjectResourceType,
  ResourceSaveCommand,
} from './projectResourceType'
import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'

const MATERIAL_DEFAULTS: Pick<IMaterialInput, 'color' | 'iconName' | 'name'> = {
  name: 'New Material',
  color: '#808080',
  iconName: 'material',
}

export interface MissingMaterialResourceRecord {
  readonly _tag: 'MissingMaterialResourceRecord'
  readonly recordId: string
}

/** Type-specific failures produced while decoding or saving Materials. */
export type MaterialResourceDomainFailure =
  | MissingMaterialResourceId
  | MissingMaterialResourceRecord

/** Complete expected-failure channel for Material resource operations. */
export type MaterialResourceFailure =
  ProjectResourceFailure<MaterialResourceDomainFailure>

/**
 * Decodes the Material list selected by the configured GraphQL operation.
 * Soft-deleted Materials are kept (flagged `isDeleted`) so surfaces holding a
 * saved id — workflow form fields above all — can still name what was picked.
 * `ProjectResourcesStore` keeps them out of the plain `records` projection.
 */
export function decodeMaterialResources(
  locations: readonly ILocationBase[],
): ResourceResult<readonly MaterialResource[], MaterialResourceDomainFailure> {
  const resources: MaterialResource[] = []

  for (const location of locations) {
    if (location.type !== LocationType.Material) continue

    const resource = parseMaterialResource(location)
    if (resource._tag === 'Err') return resource
    resources.push(resource.value)
  }

  return resourceOk(resources)
}

/** Builds the Material mutation payload without executing effects. */
export function buildMaterialSaveVariables(
  projectId: string,
  command: ResourceSaveCommand,
  resources: readonly MaterialResource[],
): ResourceResult<
  { readonly materials: readonly IMaterialInput[] },
  MissingMaterialResourceRecord
> {
  const { recordId, formData } = command
  const { templateId, rootFields, dataFields } = splitResourceFormData(
    ResourceTypeId.MATERIALS,
    formData,
    ['operatingIntervals', 'permittedCompanies'],
  )
  const isCreation = recordId === 'new' || !recordId
  const existing = isCreation
    ? null
    : (resources.find(resource => resource.id === recordId) ?? null)

  if (!isCreation && !existing) {
    return resourceErr({
      _tag: 'MissingMaterialResourceRecord',
      recordId,
    })
  }

  const input: IMaterialInput = existing
    ? {
        assignedGlobes: [...existing.assignedGlobes],
        assignedSitemaps: [...existing.assignedSitemaps],
        code: existing.code,
        color: existing.color,
        iconName: existing.iconName,
        id: recordId,
        maxBookingDuration: existing.maxBookingDuration,
        maxOverlappingDeliveries: existing.maxOverlappingDeliveries,
        name: rootFields.name ?? existing.name,
        operatingIntervals: Array.isArray(rootFields.operatingIntervals)
          ? rootFields.operatingIntervals.map(
              ({ startDate, endDate, isDaily, dayOfWeek }) => ({
                startDate,
                endDate,
                isDaily,
                dayOfWeek,
              }),
            )
          : existing.operatingIntervals.map(interval => ({ ...interval })),
        // By id only: a parentType the resource still carries is never sent (STX1-1355).
        parent: existing.parent ? { parentId: existing.parent.parentId } : null,
        permittedCompanies: Array.isArray(rootFields.permittedCompanies)
          ? rootFields.permittedCompanies
          : [...existing.permittedCompanies],
        projectId,
        templateId: templateId ?? existing.templateId,
        data: mergeResourceData(existing.data, dataFields),
      }
    : {
        ...MATERIAL_DEFAULTS,
        projectId,
        name: rootFields.name ?? MATERIAL_DEFAULTS.name,
        templateId: templateId ?? null,
        data: compactResourceData(dataFields),
      }

  return resourceOk({ materials: [input] })
}

/** Pure Material variation consumed by the shared project resource store. */
export const materialResourceType: ProjectResourceType<
  MaterialResource,
  MaterialResourceDomainFailure
> = {
  typeId: ResourceTypeId.MATERIALS,
  saveDocument: SaveMaterialsDocument,
  decodeResources: decodeMaterialResources,
  toRecord: toMaterialResourceRecord,
  buildSaveVariables: buildMaterialSaveVariables,
}
